import type { PeerConnectionEvent } from 'jssip/lib/RTCSession';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { CallOptions } from 'jssip/lib/UA';

import type { DeafPartyResolutionResult } from 'features/call/call-accepted/interfaces';
import { isStringAllLettersOrEmpty } from 'common/helpers/';
import {
  sendAnalyticsError,
  sendAnalyticsInfo,
  sendAnalyticsWarning,
} from 'features/analytics/helpers';
import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import { getCorrectUriFromList } from 'features/call/call-accepted/helpers';
import { ConnectionChangeType } from 'features/call/call-base/enums';
import { callIdSelector } from 'features/call/call-base/store';
import { removePlusFromPhoneNumber } from 'features/call/call-base/helpers';
import { CallDaoService } from 'features/call/call-base/services';
import {
  deafDialReleased,
  deafPartyResolved,
  setDeafIsLoading,
} from 'features/call/call-deaf/store';
import { claimDeafDial } from 'features/call/call-deaf/thunks';
import { primaryHearingPhoneNumberSelector } from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import {
  setCallWasPlaced,
  setShouldCreateDataChannel,
} from 'features/call/vrs-call/store/';
import {
  dialedNumbersListSelector,
  setDialedNumberHistory,
} from 'features/dialed-numbers-history/store';
import { envConfig } from 'features/environment/services';
import { useMonitorCustomHeader } from 'features/monitoring/hooks/useMonitorCustomHeader';
import { isMonitorHostIpExistsSelector } from 'features/monitoring/store/selector';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { handleError } from 'features/notification/store';
import { useConnectionRefs } from 'features/router/hooks';
import { sipSettings } from 'features/sip/config';
import { SipSessionEventBus } from 'features/sip/services';
import type { EnsureSipUserAgentResult } from 'features/sip/store';
import { ensureSipUserAgentRegistered } from 'features/sip/store';
import { isConvo, isSorenson } from 'features/vrs-third-parties/helpers';
import { ANONYMOUS } from 'features/voice-meeting/constants';
import { useSipHeaders } from 'features/sip/hooks';

export const useCallDeafParticipantHandlers = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { uaRef } = useConnectionRefs();
  const sipHeaders = useSipHeaders({ isRemoteMercuryCall: false });
  const { monitorCustomHeader } = useMonitorCustomHeader();
  const dialedNumbersList = useAppSelector(dialedNumbersListSelector);
  const isCallMonitoring = useAppSelector(isMonitorHostIpExistsSelector);
  const isVrsMode = useAppSelector(isVrsModeSelector);
  const hearingPhoneNumber = useAppSelector(primaryHearingPhoneNumberSelector);

  const resolvePhoneNumberWithCore = useCallback(
    async (
      callerNumber: string,
      calleeNumber: string
    ): Promise<DeafPartyResolutionResult> => {
      try {
        const res = await CallDaoService.resolvePhoneNumberWithCore(
          callerNumber,
          calleeNumber
        );
        const uri = getCorrectUriFromList(res?.URIList);

        return {
          uri,
          isConvo: isConvo(res?.URIList),
          isSorenson: isSorenson(res?.URIList),
          sipRemoteDomain: uri.split('@')[1] ?? 'unresolved.sorenson.com',
          name: res?.Name ?? '',
          videoMsgData: res?.VideoMsgData?.NumberRings
            ? {
                numberRings: res.VideoMsgData.NumberRings,
                greetingPreference: res.VideoMsgData.GreetingPreference,
              }
            : undefined,
        };
      } catch (e: any) {
        dispatch(
          sendAnalyticsError({
            Method: 'resolvePhoneNumberWithCore',
            Message: e?.message || 'error resolving phone number with core',
          })
        );
        return {
          uri: '',
          isConvo: false,
          isSorenson: false,
          sipRemoteDomain: 'unresolved.sorenson.com',
          name: '',
        };
      }
    },
    [dispatch]
  );

  // The active call must be read from the store at the moment of use: a handler
  // closure can outlive the call it was rendered for.
  const readActiveCallId = useCallback(
    () => dispatch((_, getState) => callIdSelector(getState())),
    [dispatch]
  );

  const handleDeafCancel = useCallback(() => {
    dispatch(setDeafIsLoading(true));

    if (uaRef?.current) {
      uaRef.current.terminateSessions({
        status_code: 200,
      });
    } else {
      // No UA means no deaf session to terminate, so the cancel is already
      // satisfied. The disconnect below must still run so callers such as
      // SignMail can move on.
      dispatch(
        sendAnalyticsWarning({
          Method: 'handleDeafCancel',
          Message: 'Deaf cancel requested without a SIP user agent',
        })
      );
    }

    ParticipantsStatusEventBus.deaf.$disconnected.next({
      connectionChangeType: ConnectionChangeType.AGENT_DISCONNECT_DEAF,
      isConnectionCallStateNeeded: false,
    });
  }, [uaRef, dispatch]);

  const connectDeaf = useCallback(
    async (phoneNumber: string, calleeConnectionString?: string) => {
      let sipRemoteDomain: string;
      const phoneNumberWithoutPlus = phoneNumber.replace(/^\+/, '');
      if (dialedNumbersList.includes(phoneNumberWithoutPlus)) {
        dispatch(
          sendAnalyticsInfo({
            Method: 'multipleDeafConnect',
            Message:
              'Attempted to dial the same deaf participant number multiple times',
            PhoneNumber: phoneNumber,
          })
        );
      }
      dispatch(
        sendAnalyticsInfo({
          Method: 'handleDeafConnect',
          Message: 'Initiating connection to deaf participant',
          PhoneNumber: phoneNumber,
        })
      );
      if (!uaRef?.current) {
        // `sip` owns the UA. While the session is live it recreates and
        // registers the UA on request; this dial never writes the intent.
        const callId = readActiveCallId();
        dispatch(setDeafIsLoading(true));
        let recovery: EnsureSipUserAgentResult;
        try {
          recovery = await dispatch(ensureSipUserAgentRegistered()).unwrap();
        } catch (e) {
          // Loading belongs to whichever call is active now; the old call's
          // loading was already reset with it.
          if (readActiveCallId() === callId) {
            dispatch(setDeafIsLoading(false));
          }
          throw e;
        }
        const { outcome, registrationStatus } = recovery;

        if (readActiveCallId() !== callId) {
          dispatch(
            sendAnalyticsInfo({
              Method: 'handleDeafConnect',
              Message:
                'Recovery finished for a different call, so the deaf dial was dropped',
              PhoneNumber: phoneNumber,
              RecoveryOutcome: outcome,
            })
          );
          return;
        }

        if (outcome !== 'registered' || !uaRef?.current) {
          dispatch(
            handleError({
              error: new Error('jssip.UA not found'),
              title: t('callErrors.deafConnectUnavailable'),
              methodName: 'handleDeafConnect',
              state: { recoveryOutcome: outcome, registrationStatus },
            })
          );
          dispatch(setDeafIsLoading(false));
          return;
        }
      }
      dispatch(
        setDialedNumberHistory([phoneNumberWithoutPlus, ...dialedNumbersList])
      );
      dispatch(setCallWasPlaced());
      dispatch(setDeafIsLoading(true));

      const settings = sipSettings.get();

      const fromUserName = hearingPhoneNumber || calleeConnectionString;

      let hearingNumberToResolveWithCore = hearingPhoneNumber;
      const isStringAllLettersResult =
        isStringAllLettersOrEmpty(hearingPhoneNumber);
      if (isStringAllLettersResult) {
        hearingNumberToResolveWithCore = ANONYMOUS;
      }

      let extraHeaders = sipHeaders;

      // Adding monitoring host IP address for the monitored agent
      if (isCallMonitoring && extraHeaders?.length) {
        extraHeaders = extraHeaders.concat(monitorCustomHeader);
      }

      const options: CallOptions = {
        extraHeaders,
        pcConfig: settings.pcConfig,
        mediaConstraints: {
          audio: true,
          video: true,
        },
        eventHandlers: {
          peerconnection: (event: PeerConnectionEvent) => {
            SipSessionEventBus.peerConnection$.next(event);
          },
        },
        ...(fromUserName ? { fromUserName } : {}),
      };
      if (isVrsMode) {
        const resolveInfo = await resolvePhoneNumberWithCore(
          removePlusFromPhoneNumber(hearingNumberToResolveWithCore),
          removePlusFromPhoneNumber(phoneNumber)
        );
        if (resolveInfo) {
          dispatch(deafPartyResolved(resolveInfo));
        }
        sipRemoteDomain = resolveInfo.sipRemoteDomain;
        if (resolveInfo.isConvo) {
          console.log('DEBUG: Changing behavior for Convo. No datachannel.', {
            resolveInfo,
          });
          delete options.eventHandlers;
          dispatch(setShouldCreateDataChannel(false));
        } else {
          dispatch(setShouldCreateDataChannel(true));
        }
      } else {
        sipRemoteDomain = envConfig.vri.sipCustomerEndpoint;
      }

      const callee = `sip:+${phoneNumberWithoutPlus}@${sipRemoteDomain}`.trim();

      try {
        // Read the ref at dial time: the session can end, and stopUA can null
        // it, while the number resolve above is in flight.
        const userAgent = uaRef?.current;
        if (!userAgent) {
          throw new Error('jssip.UA not found');
        }
        userAgent.call(callee, options);
        ParticipantsStatusEventBus.deaf.$connecting.next();
      } catch (e: any) {
        dispatch(
          sendAnalyticsError({
            Method: 'handleDeafConnect',
            Message: e?.message || 'error connecting to deaf party',
          })
        );
        handleDeafCancel();
      }
    },

    [
      dialedNumbersList,
      dispatch,
      t,
      uaRef,
      readActiveCallId,
      hearingPhoneNumber,
      sipHeaders,
      isCallMonitoring,
      isVrsMode,
      monitorCustomHeader,
      resolvePhoneNumberWithCore,
      handleDeafCancel,
    ]
  );

  // Initiate-dial, hearing reconnect, emergency and caller reconnect all end up
  // here, and recovery makes a dial long enough for them to overlap. Only one
  // dial may be in flight; the claim is released on every exit.
  const handleDeafConnect = useCallback(
    async (phoneNumber: string, calleeConnectionString?: string) => {
      const claimToken = dispatch(claimDeafDial());

      if (claimToken === null) {
        dispatch(
          sendAnalyticsInfo({
            Method: 'handleDeafConnect',
            Message:
              'Ignored deaf connect request while another dial is in flight',
            PhoneNumber: phoneNumber,
          })
        );
        return;
      }

      try {
        await connectDeaf(phoneNumber, calleeConnectionString);
      } finally {
        dispatch(deafDialReleased(claimToken));
      }
    },
    [dispatch, connectDeaf]
  );

  const handleDeafDisconnect = () => {
    dispatch(setDeafIsLoading(true));
    SipSessionEventBus.terminate$.next();
  };

  return {
    handleDeafConnect,
    handleDeafCancel,
    handleDeafDisconnect,
  };
};
